import React from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { HistoryRow } from "../../Api/communicationHistoryApi";
import { B13, B13Ink, B13TypeScale } from "../../theme/b13Tokens";

/**
 * Shared timeline used by Student/Parent/Staff profiles + the central
 * Communication History detail drawer.
 *
 * Never labels "Read" — provider gives no read evidence. §40 explicitly
 * requires "Opened" + tooltip. `delivered` never renders unless the
 * server row actually says delivered, which only happens if a
 * verified provider callback ever fires.
 */
interface Props {
  rows: HistoryRow[];
  loading?: boolean;
  emptyText?: string;
}

const STATUS_STYLE: Record<string, { bg: string; fg: string; label?: string }> = {
  queued:     { bg: "#FEF3C7", fg: "#92400E" },
  sent:       { bg: "#DCFCE7", fg: "#15803D" },
  delivered:  { bg: "#DCFCE7", fg: "#15803D" },
  failed:     { bg: "#FEE2E2", fg: "#B91C1C" },
  suppressed: { bg: "#F1F5F9", fg: "#475569", label: "Skipped" },
  cancelled:  { bg: "#F1F5F9", fg: "#475569" },
};

const CommunicationTimeline: React.FC<Props> = ({ rows, loading, emptyText }) => {
  if (loading) {
    return <Typography sx={{ color: B13Ink.ink2 }}>Loading communication history…</Typography>;
  }
  if (! rows || rows.length === 0) {
    return (
      <Typography sx={{ color: B13Ink.ink2 }}>
        {emptyText ?? "No communication history yet."}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      {rows.map((r) => {
        const Icon = r.channel === "email" ? EmailOutlinedIcon : SmsOutlinedIcon;
        const tone = r.channel === "email" ? "#1F6FEB" : "#22c55e";
        const statusStyle = STATUS_STYLE[r.status] ?? STATUS_STYLE.sent;
        return (
          <Box key={`${r.channel}-${r.id}`}
            sx={{ p: 1.5, border: `1px solid ${B13.line}`, borderRadius: 2,
                  bgcolor: "#fff", display: "flex", gap: 1.5, alignItems: "flex-start" }}
          >
            <Box sx={{ width: 36, height: 36, borderRadius: 2,
                       display: "flex", alignItems: "center", justifyContent: "center",
                       bgcolor: `${tone}1A`, color: tone, flexShrink: 0 }}>
              <Icon fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: B13.ink, minWidth: 0 }}>
                  {r.subject || (r.channel === "sms" ? "SMS message" : "(no subject)")}
                </Typography>
                <Chip size="small"
                  label={r.channel.toUpperCase()}
                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: `${tone}14`, color: tone }} />
                <Chip size="small" label={(statusStyle.label ?? r.status)}
                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700,
                        bgcolor: statusStyle.bg, color: statusStyle.fg,
                        textTransform: "capitalize" }} />
                {r.is_test && (
                  <Chip size="small" label="Test"
                    sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#E0E7FF", color: "#3730A3" }} />
                )}
                {r.channel === "email" && r.opened_at && (
                  <Tooltip title="Open tracking is approximate and may be affected by email privacy features.">
                    <Chip size="small"
                      label={`Opened${r.open_count > 1 ? ` · ${r.open_count}×` : ""}`}
                      sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#EDE9FE", color: "#5B21B6" }} />
                  </Tooltip>
                )}
              </Stack>
              <Typography sx={{ fontSize: 12.5, color: B13Ink.ink2, mt: 0.5 }} noWrap>
                {r.body_preview || "—"}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: B13Ink.ink3, mt: 0.5 }}>
                {new Date(r.created_at).toLocaleString()}
                {r.recipient_address ? ` · ${r.recipient_address}` : ""}
                {r.campaign_id ? ` · Campaign #${r.campaign_id}` : ""}
                {r.failure_reason ? ` · ${r.failure_reason}` : ""}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default CommunicationTimeline;
